import axios from "axios";

// This function fetches the enrollment status for a course or track
export const fetchEnrollmentStatus = async ({
  courseId,
  trackId,
  signal,
}: {
  courseId?: any;
  trackId?: any;
  signal?: AbortSignal;
}) => {
  // Validate input
  if (!(courseId || trackId) || (courseId && trackId)) {
    throw new Error("Provide exactly one of courseId or trackId");
  }

  // Ensure we have a token
  const token = localStorage.getItem("access");
  if (!token) {
    console.error("User not authenticated");
    return null;
  }

  // Build URL + query params
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `${baseUrl}/course/bk-enrollment-status/`;
  const params: any = {};
  if (courseId) params.course_id = courseId;
  if (trackId) params.track_id = trackId;

  // Axios config with JWT auth
  const config = {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `JWT ${token}`,
    },
    params,
    signal,
  };

  try {
    const response = await axios.get(endpoint, config);
    return response.data; // { enrolled, paid, payment_status, detail }
  } catch (err: any) {
    if (axios.isCancel(err)) throw err;
    console.error("Failed to fetch enrollment status:", err);
    throw err;
  }
};

// This function fetches learning content for a course or track
export async function fetchLearningContent({
  courseId,
  trackId,
  signal,
}: {
  courseId?: any;
  trackId?: any;
  signal?: AbortSignal;
}) {
  // Validate inputs
  if (!(courseId || trackId) || (courseId && trackId)) {
    throw new Error("Provide exactly one of courseId or trackId");
  }

  // Retrieve JWT
  const token = localStorage.getItem("access");
  if (!token) {
    console.error("User not authenticated");
    return null;
  }

  // Build request URL and params
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `${baseUrl}/course/bk-learning-content/`;
  const config: any = {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `JWT ${token}`,
    },
    params: {},
    signal,
  };
  if (courseId) config.params.course_id = courseId;
  if (trackId) config.params.track_id = trackId;

  // Fetch
  const response = await axios.get(endpoint, config);
  return response.data;
}

export const handleCourseTrackEnrollment = async ({
  courseId,
  trackId,
}: {
  courseId?: any;
  trackId?: any;
}) => {
  // Validate input
  if (!(courseId || trackId) || (courseId && trackId)) {
    throw new Error("Provide exactly one of courseId or trackId");
  }

  // Ensure we have a token
  const token = localStorage.getItem("access");
  if (!token) {
    console.error("User not authenticated");
    return null;
  }

  // Build request URL and payload
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `${baseUrl}/course/bk-online-enrollment`;

  const payload: any = {};
  if (courseId) payload.course_id = courseId;
  if (trackId) payload.track_id = trackId;

  const config = {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `JWT ${token}`,
    },
  };

  try {
    const response = await axios.post(endpoint, payload, config);
    return response.data; // { message, enrollment_status, ... }
  } catch (err: any) {
    console.error("Enrollment failed:", err.response?.data || err.message);
    throw err;
  }
};

export const fetchEnrollmentData = async (signal?: AbortSignal) => {
  const token = localStorage.getItem("access");
  if (!token) {
    return [];
  }

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `JWT ${token}`,
      Accept: "application/json",
    },
    signal,
  };

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/course/bk-online-enrollment`,
      config
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch (error: any) {
    if (axios.isCancel(error)) throw error;
    console.error("Failed to fetch enrollment data:", error);
    throw error;
  }
};

export const fetchPaymentHistory = async (signal?: AbortSignal) => {
  const token = localStorage.getItem("access");
  if (!token) {
    return [];
  }

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `JWT ${token}`,
      Accept: "application/json",
    },
    signal,
  };

  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}/course/bk-payment-history`,
    config
  );
  return Array.isArray(response.data) ? response.data : [];
};

export const fetchTrackDetailBySlug = async (slug: string, signal?: AbortSignal) => {
  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}/course/bk-track/${slug}`,
    { signal }
  );
  return response.data;
};

export const fetchCourseDetailBySlug = async (slug: string, isTrack: boolean, signal?: AbortSignal) => {
  const url = isTrack
    ? `${process.env.REACT_APP_API_URL}/course/bk-track/${slug}`
    : `${process.env.REACT_APP_API_URL}/course/bk-course/${slug}`;
  const response = await axios.get(url, { signal });
  return response.data;
};
